/**
 * Popup "O Google recusou respostas da IA". Decisão de 12/10/2026: só a equipe vê,
 * e só quem o backend libera (reviews.recusadas.alerta.emails — hoje o Thiago
 * Carvalho). Para os demais a lista vem vazia e nada aparece.
 */
import { useCallback, useEffect, useState } from "react";
import { ApiService } from "../../config";
import useUserStore from "../../store/user";
import { textoDoMotivo, type ItemRecusada } from "./motivo";

const INTERVALO_MS = 30 * 60 * 1000;

const ESTRELAS: Record<string, number> = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };

const dataCurta = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

export const AlertaDeRecusadas = () => {
  const { user } = useUserStore();
  const [itens, setItens] = useState<ItemRecusada[]>([]);
  const [aberto, setAberto] = useState(false);

  const buscar = useCallback(async () => {
    try {
      const resp = await ApiService.get("/reviews/recusadas/alerta");
      const lista: ItemRecusada[] = resp?.data?.data?.itens ?? [];
      setItens(lista);
      if (lista.length > 0) setAberto(true);
    } catch {
      // Sem permissão ou fora do ar: o popup simplesmente não aparece.
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    buscar();
    const id = window.setInterval(buscar, INTERVALO_MS);
    return () => window.clearInterval(id);
  }, [user, buscar]);

  const entendi = async () => {
    setAberto(false);
    setItens([]);
    try {
      await ApiService.post("/reviews/recusadas/alerta/visto");
    } catch {
      // Se não gravar, o popup volta na próxima consulta — melhor que perder o aviso.
    }
  };

  if (!aberto || itens.length === 0) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="gr-recusadas-titulo"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1900,
        background: "rgba(32,33,36,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          width: "100%",
          maxWidth: 560,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          fontFamily: "inherit",
        }}
      >
        <div style={{ padding: "20px 24px 12px", borderBottom: "1px solid #E3E3E3" }}>
          <h2 id="gr-recusadas-titulo" style={{ margin: 0, fontSize: 19, color: "#1F1F1F" }}>
            {itens.length === 1
              ? "O Google recusou 1 resposta da IA"
              : `O Google recusou ${itens.length} respostas da IA`}
          </h2>
          <p style={{ margin: "6px 0 0", color: "#5F6368", fontSize: 14, lineHeight: 1.5 }}>
            Só a equipe vê este aviso; o cliente não. Confira e responda de novo pela tela de avaliações do perfil.
          </p>
        </div>

        <ul style={{ listStyle: "none", margin: 0, padding: "8px 24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
          {itens.map((item) => (
            <li
              key={item.reviewExternalId}
              style={{ border: "1px solid #E3E3E3", borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 6 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <strong style={{ color: "#1F1F1F", fontSize: 14.5 }}>{item.perfil ?? "Perfil"}</strong>
                <span style={{ color: "#5F6368", fontSize: 12.5 }}>
                  {item.autor ?? "Cliente"}
                  {item.estrelas && ESTRELAS[item.estrelas] ? ` · ${ESTRELAS[item.estrelas]}★` : ""}
                  {item.recusadaEm ? ` · recusada em ${dataCurta(item.recusadaEm)}` : ""}
                </span>
              </div>
              <span style={{ color: "#B3261E", fontSize: 13 }}>{textoDoMotivo(item)}</span>
              {item.resposta && (
                <span style={{ color: "#3C4043", fontSize: 13, lineHeight: 1.45, overflowWrap: "anywhere" }}>
                  Resposta recusada: “{item.resposta.length > 220 ? `${item.resposta.slice(0, 220)}…` : item.resposta}”
                </span>
              )}
              {item.linkNoGoogle && (
                <a href={item.linkNoGoogle} target="_blank" rel="noopener noreferrer" style={{ color: "#1264C7", fontSize: 13 }}>
                  Ver no Google
                </a>
              )}
            </li>
          ))}
        </ul>

        <div style={{ padding: "12px 24px 20px", display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #E3E3E3" }}>
          <button
            type="button"
            onClick={() => setAberto(false)}
            style={{ background: "transparent", color: "#1264C7", border: "1px solid #C4C7C5", borderRadius: 8, padding: "9px 14px", cursor: "pointer" }}
          >
            Ver depois
          </button>
          <button
            type="button"
            onClick={entendi}
            style={{ background: "#1264C7", color: "#fff", border: 0, borderRadius: 8, padding: "9px 16px", cursor: "pointer" }}
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
};
